<template>
  <nav class="navbar">
    <div class="container">
      <h1 class="logo">
        <router-link to="/">
          Vocablly
        </router-link>
      </h1>

      <!-- Desktop links -->
      <div class="nav-links-desktop">
        <button
          v-if="loggedInUser"
          class="nav-btn nav-btn-secondary"
          @click="goToDashboard"
        >
          Dashboard
        </button>

        <template v-if="hasSessionAccess">
          <button
            class="nav-btn nav-btn-primary"
            @click="logout"
          >
            Cerrar Sesión
          </button>
        </template>

        <template v-else>
          <button
            class="nav-btn nav-btn-secondary"
            @click="login"
          >
            Iniciar Sesión
          </button>
          <button
            class="nav-btn nav-btn-primary"
            @click="signup"
          >
            Crear Cuenta
          </button>
        </template>
      </div>

      <!-- Hamburger menu icon (mobile) -->
      <button
        ref="menuToggle"
        class="menu-toggle"
        aria-label="Toggle navigation menu"
        @click="toggleMenu"
      >
        <span class="bar" />
        <span class="bar" />
        <span class="bar" />
      </button>
    </div>

    <!-- Mobile Glassmorphism Overlay Menu -->
    <transition name="fade">
      <div
        v-if="isMenuOpen"
        ref="navOverlay"
        class="nav-overlay"
        @click.self="closeMenu"
      >
        <div
          ref="navLinks"
          class="nav-overlay-content"
        >
          <div class="overlay-header">
            <div class="overlay-logo">
              Vocablly
            </div>
            <button
              class="overlay-close"
              aria-label="Close menu"
              @click="closeMenu"
            >
              ×
            </button>
          </div>

          <div class="overlay-buttons">
            <button
              v-if="loggedInUser"
              class="nav-btn nav-btn-secondary nav-btn-full"
              @click="goToDashboard"
            >
              Dashboard
            </button>

            <template v-if="hasSessionAccess">
              <button
                class="nav-btn nav-btn-primary nav-btn-full"
                @click="logout"
              >
                Cerrar Sesión
              </button>
            </template>

            <template v-else>
              <button
                class="nav-btn nav-btn-secondary nav-btn-full"
                @click="login"
              >
                Iniciar Sesión
              </button>
              <button
                class="nav-btn nav-btn-primary nav-btn-full"
                @click="signup"
              >
                Crear Cuenta
              </button>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { mapGetters } from "vuex";
import {
  clearTeacherCodeAccess,
  getTeacherCodeAccess,
  getTeacherCodeRemainingMs,
} from "../utils/teacherCodeAccess.js";

export default {
  data() {
    return {
      isMenuOpen: false,
      teacherCodeAccess: null,
      teacherCodeTimeoutId: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["loggedInUser"]),
    hasSessionAccess() {
      return !!this.loggedInUser || !!this.teacherCodeAccess;
    },
  },
  watch: {
    $route() {
      this.syncTeacherCodeAccess();
      this.closeMenu();
    },
  },
  mounted() {
    this.syncTeacherCodeAccess();
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("storage", this.syncTeacherCodeAccess);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("storage", this.syncTeacherCodeAccess);
    this.clearTeacherCodeTimeout();
  },
  methods: {
    syncTeacherCodeAccess() {
      this.teacherCodeAccess = getTeacherCodeAccess();
      this.scheduleTeacherCodeTimeout();
    },
    clearTeacherCodeAccessLocally() {
      clearTeacherCodeAccess();
      this.teacherCodeAccess = null;
      this.clearTeacherCodeTimeout();
    },
    clearTeacherCodeTimeout() {
      if (this.teacherCodeTimeoutId) {
        clearTimeout(this.teacherCodeTimeoutId);
        this.teacherCodeTimeoutId = null;
      }
    },
    scheduleTeacherCodeTimeout() {
      this.clearTeacherCodeTimeout();

      if (!this.teacherCodeAccess) return;

      const remainingMs = getTeacherCodeRemainingMs();

      if (remainingMs <= 0) {
        this.handleTeacherCodeSessionExpired();
        return;
      }

      this.teacherCodeTimeoutId = window.setTimeout(() => {
        this.handleTeacherCodeSessionExpired();
      }, remainingMs);
    },
    async handleTeacherCodeSessionExpired() {
      if (!this.teacherCodeAccess) {
        clearTeacherCodeAccess();
        this.clearTeacherCodeTimeout();
        return;
      }

      this.clearTeacherCodeAccessLocally();
      this.isMenuOpen = false;

      if (this.$route.path !== "/login") {
        try {
          await this.$router.replace("/login");
        } catch (error) {
          console.error("Error redirecting after teacher code session expired:", error);
        }
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    handleClickOutside(event) {
      if (!this.isMenuOpen) return;

      const menuToggle = this.$refs.menuToggle;
      const navOverlay = this.$refs.navOverlay;

      if (!menuToggle || !navOverlay) return;

      if (
        !menuToggle.contains(event.target) &&
        !navOverlay.contains(event.target)
      ) {
        this.isMenuOpen = false;
      }
    },
    async logout() {
      try {
        if (this.loggedInUser) {
          await this.$store.dispatch("auth/logout");
        }

        this.clearTeacherCodeAccessLocally();
        this.isMenuOpen = false;
        await this.$router.push("/login");
      } catch (error) {
        console.error("Error during logout:", error);
      }
    },
    login() {
      this.isMenuOpen = false;
      this.$router.push("/login");
    },
    signup() {
      this.isMenuOpen = false;
      this.$router.push("/signup");
    },
    goToDashboard() {
      this.isMenuOpen = false;
      this.$router.push("/dashboard");
    },
  },
};
</script>

<style scoped>
.navbar {
  padding: 0px 40px;
  background: linear-gradient(135deg, #0099ff, #27a7fc);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.25);
  position: sticky;
  top: 0;
  z-index: 50;
}

.container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1000px;
  margin: auto;
  position: relative;
}

.logo {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 1px;
}

.logo a {
  text-decoration: none;
  color: #ffffff;
}

.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 50px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
}

.menu-toggle .bar {
  height: 3px;
  width: 28px;
  background: #ffffff;
  border-radius: 10px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.nav-links-desktop {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.nav-btn {
  cursor: pointer;
  padding: 0.55rem 1.2rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  border: 1px solid transparent;
  transition: all 0.2s ease-out;
  outline: none;
  white-space: nowrap;
}

.nav-btn-primary {
  background: #ffffff;
  color: #00528f;
  border-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.nav-btn-primary:hover {
  background: #f5f9ff;
  border-color: #ffffff;
  transform: translateY(-1px);
}

.nav-btn-secondary {
  background: transparent;
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.8);
}

.nav-btn-secondary:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: #ffffff;
  transform: translateY(-1px);
}

.nav-btn-full {
  width: 100%;
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.nav-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 80px;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
}

.nav-overlay-content {
  width: min(90%, 420px);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 1.4rem 1.2rem 1.7rem;
  color: #ffffff;
}

.overlay-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.overlay-logo {
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 1px;
}

.overlay-close {
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.3rem;
  border-radius: 999px;
  transition: background 0.15s ease;
}

.overlay-close:hover {
  background: rgba(255, 255, 255, 0.15);
}

.overlay-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

@media (max-width: 768px) {
  .navbar {
    padding: 0px 20px;
  }

  .menu-toggle {
    display: flex;
  }

  .nav-links-desktop {
    display: none;
  }
}
</style>